import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Modal, ModalBody } from "reactstrap";
import { PAGE_TEMPLATES } from "../../../cores/websitePage/constants/blockTypes";
import { createHtmlPageBlockContent } from "../../../cores/websitePage/constants/htmlPageBlock";
import { WebsitePageContent } from "../../../cores/websitePage/interfaces/WebsiteBlock";
import { AiWebsiteFormData, CreateAiWebsiteModal } from "./CreateAiWebsiteModal";
import { WebsiteIcon } from "./WebsiteIcon";
import "./CreatePageModal.scss";
import "./website-builder.scss";

export interface CreatePagePayload {
  name: string;
  templateId: string;
  contentJson?: WebsitePageContent;
}

interface Props {
  isOpen: boolean;
  isAiGenerating?: boolean;
  onClose: () => void;
  onCreate: (payload: CreatePagePayload) => Promise<void>;
  onAiGenerate: (pageName: string, form: AiWebsiteFormData) => void;
}

const TEMPLATE_META: Record<string, { icon: string; descKey: string; gradient: string }> = {
  blank: {
    icon: "file",
    descKey: "app.websitePage.templates.blankDesc",
    gradient: "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)",
  },
  business: {
    icon: "briefcase",
    descKey: "app.websitePage.templates.businessDesc",
    gradient: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
  },
  landing: {
    icon: "rocket",
    descKey: "app.websitePage.templates.landingDesc",
    gradient: "linear-gradient(135deg, #7c3aed 0%, #db2777 100%)",
  },
  realestate: {
    icon: "home",
    descKey: "app.websitePage.templates.realestateDesc",
    gradient: "linear-gradient(135deg, #0b1f3a 0%, #1c7ed6 100%)",
  },
  artisan: {
    icon: "leaf",
    descKey: "app.websitePage.templates.artisanDesc",
    gradient: "linear-gradient(135deg, #152417 0%, #bedb70 100%)",
  },
  "custom-code": {
    icon: "code",
    descKey: "app.websitePage.templates.customCodeDesc",
    gradient: "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
  },
  "custom-ai": {
    icon: "wand-magic-sparkles",
    descKey: "app.websitePage.templates.customAiDesc",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #4f46e5 100%)",
  },
};

const DEFAULT_HTML = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Mi página</title>
  <style>
    body { margin: 0; font-family: system-ui, sans-serif; }
    .hero { padding: 80px 24px; text-align: center; background: linear-gradient(135deg, #4f46e5, #7c3aed); color: #fff; }
    @media (max-width:768px){.wp-navbar__menu-btn{display:block!important;margin-left:auto}.wp-navbar__nav{display:none!important;position:absolute;top:calc(100% + 1px);left:0;right:0;flex-direction:column;align-items:stretch;padding:12px 24px 16px;background:#fff;border-bottom:1px solid rgba(15,23,42,0.08);box-shadow:0 12px 32px rgba(15,23,42,0.1)}.wp-navbar__nav.open{display:flex!important}.wp-navbar__cta{margin-left:0!important;margin-top:8px;text-align:center}}
  </style>
</head>
<body>
  <header class="wp-navbar wp-navbar--sticky" style="background:#fff;color:#0f172a;border-bottom:1px solid rgba(15,23,42,0.08);position:sticky;top:0;z-index:1000">
    <div class="wp-navbar__inner" style="max-width:1100px;margin:0 auto;padding:0 24px;min-height:72px;display:flex;align-items:center;justify-content:space-between;gap:16px;position:relative">
      <a href="#inicio" class="wp-navbar__brand" style="display:flex;align-items:center;gap:10px;text-decoration:none;color:inherit;font-weight:800">
        <span class="wp-navbar__logo" style="width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:rgba(79,70,229,0.12);color:#4f46e5;font-weight:800">M</span>
        <span>Mi Marca</span>
      </a>
      <button type="button" class="wp-navbar__menu-btn" aria-label="Abrir menú" aria-expanded="false" data-wp-nav-toggle="wp-nav-default" style="display:none;background:none;border:none;font-size:1.45rem;cursor:pointer;padding:8px">☰</button>
      <nav class="wp-navbar__nav" id="wp-nav-default" aria-label="Navegación principal" style="display:flex;align-items:center;gap:4px">
        <a href="#inicio" style="text-decoration:none;color:inherit;font-weight:600;padding:8px 12px">Inicio</a>
        <a href="#servicios" style="text-decoration:none;color:inherit;font-weight:600;padding:8px 12px">Servicios</a>
        <a href="#contacto" style="text-decoration:none;color:inherit;font-weight:600;padding:8px 12px">Contacto</a>
        <a href="#contacto" class="wp-navbar__cta" style="margin-left:8px;border-radius:999px;padding:10px 18px;font-weight:700;background:#4f46e5;color:#fff;text-decoration:none">Comenzar</a>
      </nav>
    </div>
  </header>
  <section class="hero" id="inicio">
    <h1>Mi página</h1>
    <p>Edita este HTML con tu contenido.</p>
  </section>
  <script>(function(){var btn=document.querySelector('[data-wp-nav-toggle="wp-nav-default"]');var nav=document.getElementById('wp-nav-default');if(!btn||!nav)return;btn.addEventListener('click',function(){var open=nav.classList.toggle('open');btn.setAttribute('aria-expanded',open?'true':'false');});document.addEventListener('click',function(e){if(!nav.classList.contains('open'))return;if(nav.contains(e.target)||btn.contains(e.target))return;nav.classList.remove('open');btn.setAttribute('aria-expanded','false');});})();</script>
</body>
</html>`;

export function CreatePageModal({ isOpen, isAiGenerating = false, onClose, onCreate, onAiGenerate }: Props) {
  const intl = useIntl();
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("business");
  const [customHtml, setCustomHtml] = useState(DEFAULT_HTML);
  const [isCreating, setIsCreating] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);

  const reset = () => {
    setName("");
    setTemplateId("business");
    setCustomHtml(DEFAULT_HTML);
    setShowAiModal(false);
  };

  const handleClose = () => {
    if (isAiGenerating) {
      setShowAiModal(false);
      onClose();
      return;
    }
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!name.trim() || isCreating) return;

    if (templateId === "custom-ai") {
      setShowAiModal(true);
      return;
    }

    setIsCreating(true);
    try {
      const payload: CreatePagePayload = {
        name: name.trim(),
        templateId,
      };
      if (templateId === "custom-code") {
        payload.contentJson = createHtmlPageBlockContent(customHtml, name.trim());
        payload.templateId = "custom-code";
      }
      await onCreate(payload);
      reset();
      onClose();
    } finally {
      setIsCreating(false);
    }
  };

  const handleAiSubmit = (form: AiWebsiteFormData) => {
    onAiGenerate(name.trim(), form);
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={handleClose} className="wp-create-modal" centered size="lg">
        <div className="wp-create-modal__header">
          <div>
            <h2>{intl.formatMessage({ id: "app.websitePage.create.title" })}</h2>
            <p>{intl.formatMessage({ id: "app.websitePage.create.subtitle" })}</p>
          </div>
          <button type="button" className="wp-create-modal__close" onClick={handleClose}>
            <WebsiteIcon icon="times" />
          </button>
        </div>
        <ModalBody className="wp-create-modal__body">
          <div className="wp-create-modal__field">
            <label>{intl.formatMessage({ id: "app.websitePage.create.name" })}</label>
            <input
              className="wp-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={intl.formatMessage({ id: "app.websitePage.create.namePlaceholder" })}
              autoFocus
            />
          </div>

          <div className="wp-create-modal__field">
            <label>{intl.formatMessage({ id: "app.websitePage.create.chooseTemplate" })}</label>
            <div className="wp-create-modal__templates">
              {PAGE_TEMPLATES.map((t) => {
                const meta = TEMPLATE_META[t.id] || TEMPLATE_META.blank;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`wp-create-modal__template ${templateId === t.id ? "wp-create-modal__template--active" : ""}`}
                    onClick={() => setTemplateId(t.id)}
                  >
                    <div className="wp-create-modal__template-preview" style={{ background: meta.gradient }}>
                      <WebsiteIcon icon={meta.icon} />
                    </div>
                    <div className="wp-create-modal__template-info">
                      <strong>{intl.formatMessage({ id: t.labelKey })}</strong>
                      <span>{intl.formatMessage({ id: meta.descKey })}</span>
                    </div>
                    {templateId === t.id && (
                      <div className="wp-create-modal__template-check">
                        <WebsiteIcon icon="check" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {templateId === "custom-code" && (
            <div className="wp-create-modal__field">
              <label>{intl.formatMessage({ id: "app.websitePage.create.pasteHtml" })}</label>
              <textarea
                className="wp-input wp-create-modal__html"
                rows={12}
                value={customHtml}
                onChange={(e) => setCustomHtml(e.target.value)}
                spellCheck={false}
              />
            </div>
          )}

          {templateId === "custom-ai" && (
            <div className="wp-create-modal__hint">
              <WebsiteIcon icon="info-circle" />
              {intl.formatMessage({ id: "app.websitePage.create.aiHint" })}
            </div>
          )}
        </ModalBody>
        <div className="wp-create-modal__footer">
          <button type="button" className="wp-btn wp-btn--secondary" onClick={handleClose}>
            {intl.formatMessage({ id: "app.websitePage.cancel" })}
          </button>
          <button
            type="button"
            className="wp-btn wp-btn--primary"
            onClick={handleCreate}
            disabled={!name.trim() || isCreating || isAiGenerating}
          >
            <WebsiteIcon icon={templateId === "custom-ai" ? "wand-magic-sparkles" : "plus"} />
            {isCreating
              ? intl.formatMessage({ id: "app.websitePage.create.creating" })
              : templateId === "custom-ai"
              ? intl.formatMessage({ id: "app.websitePage.create.configureAi" })
              : intl.formatMessage({ id: "app.websitePage.create.submit" })}
          </button>
        </div>
      </Modal>

      <CreateAiWebsiteModal
        isOpen={showAiModal}
        isGenerating={isAiGenerating}
        onClose={() => setShowAiModal(false)}
        onSubmit={handleAiSubmit}
      />
    </>
  );
}
